import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import i18n from "@/i18n";
import { EMPTY_VALUE, formatDateTime, formatNumber } from "@/i18n/format";
import type { MatchingPipelineEvent, MatchingRunDetail } from "@/services/moderators/generated/model";
import {
  getModeratorsMatchingRunsDetailQueryKey,
  useModeratorsMatchingRunsDetail,
  useModeratorsMatchingRunsRetry,
} from "@/services/moderators/generated/moderators-matching/moderators-matching";
import "./ModeratorPage.css";
import "./ModeratorMatchingPage.css";
import "./ModeratorMatchingRunsPage.css";

const STAGES = [
  "started",
  "query_embedded",
  "vector_search",
  "ranking",
  "threshold_filter",
  "persisted",
  "completed",
  "failed",
] as const;
type Stage = (typeof STAGES)[number];
const isStage = (s: string): s is Stage => (STAGES as readonly string[]).includes(s);

const stageLabel = (stage: string): string => (isStage(stage) ? i18n.t(`deals:pipeline.stage.${stage}`) : stage);

const fmtDuration = (ms: number | null | undefined): string => {
  if (ms == null) return EMPTY_VALUE;
  if (ms < 1000) return i18n.t("deals:pipeline.durationMs", { value: formatNumber(ms) });
  return i18n.t("deals:pipeline.durationS", {
    value: formatNumber(ms / 1000, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  });
};

const fmtDate = (iso: string | null | undefined): string => formatDateTime(iso);

const ModeratorMatchingRunDetailPage = () => (
  <ModeratorLayout>{(moderator) => <DetailContent isSuper={moderator.role === "SUPERMODERATOR"} />}</ModeratorLayout>
);

const DetailContent = ({ isSuper }: { isSuper: boolean }) => {
  const { t } = useTranslation(["deals", "common"]);
  const { id } = useParams<{ id: string }>();
  const matchRequestId = id ? Number(id) : Number.NaN;

  const { data, isLoading, error } = useModeratorsMatchingRunsDetail(matchRequestId, {
    query: { enabled: isSuper && Number.isFinite(matchRequestId) },
  });
  const run = data as MatchingRunDetail | undefined;
  const queryClient = useQueryClient();
  const retryMutation = useModeratorsMatchingRunsRetry({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getModeratorsMatchingRunsDetailQueryKey(matchRequestId) }),
    },
  });

  if (!isSuper) {
    return (
      <>
        <header className="mod-page__header">
          <div>
            <h1 className="mod-page__title">{t("runDetail.title")}</h1>
            <p className="mod-page__subtitle">{t("shared.superOnly")}</p>
          </div>
        </header>
        <div className="mod-page__empty">{t("runDetail.restricted")}</div>
      </>
    );
  }

  return (
    <div className="moderator-matching">
      <Link to="/moderator/matching/runs" className="moderator-matching-runs__back">
        <i className="fa-solid fa-arrow-left" /> {t("runDetail.backToRuns")}
      </Link>

      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">
            {run?.requirementTitle ||
              (Number.isFinite(matchRequestId)
                ? t("runDetail.runFallback", { id: matchRequestId })
                : t("runDetail.title"))}
          </h1>
          <p className="mod-page__subtitle">{t("runDetail.subtitle")}</p>
        </div>
      </header>

      {isLoading ? <div className="mod-page__loading">{t("runDetail.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("runDetail.loadError")}</div> : null}
      {!isLoading && !run ? <div className="mod-page__empty">{t("runDetail.notFound")}</div> : null}

      {run && (
        <>
          <section className="moderator-matching__panel">
            <header className="moderator-matching__panel-header">
              <h2>{t("runDetail.summary.title")}</h2>
              <p className="moderator-matching__hint">{t("runDetail.summary.hint")}</p>
            </header>
            <div className="moderator-matching-runs__summary-grid">
              <SummaryCell label={t("runDetail.summary.matchRequestId")} value={`#${run.matchRequestId}`} />
              <SummaryCell label={t("runDetail.summary.status")}>
                <span className={`moderator-matching-runs__status moderator-matching-runs__status--${run.status}`}>
                  {run.status}
                </span>
              </SummaryCell>
              <SummaryCell label={t("runDetail.summary.created")} value={fmtDate(run.createdAt)} />
              <SummaryCell
                label={t("runDetail.summary.requesterCompany")}
                value={run.requesterCompanyName || EMPTY_VALUE}
              />
              <SummaryCell
                label={t("runDetail.summary.requesterCompanyId")}
                value={run.requesterCompanyId != null ? `#${run.requesterCompanyId}` : EMPTY_VALUE}
              />
              <SummaryCell label={t("runDetail.summary.events")} value={formatNumber(run.events.length)} />
            </div>
            {run.status === "failed" && (
              <div className="moderator-matching-runs__event moderator-matching-runs__event--failed">
                <div className="moderator-matching-runs__event-error">
                  <Trans
                    t={t}
                    i18nKey="runDetail.whyFailed"
                    values={{ reason: run.failureReason || t("runDetail.noReason") }}
                    components={{ strong: <strong /> }}
                  />
                </div>
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--primary"
                  onClick={() => retryMutation.mutate({ matchRequestId })}
                  disabled={retryMutation.isPending}
                >
                  {retryMutation.isPending ? t("runDetail.retrying") : t("runDetail.retry")}
                </button>
                {retryMutation.isError ? (
                  <div className="mod-page__inline-error">{t("runDetail.retryFailed")}</div>
                ) : null}
              </div>
            )}
          </section>

          <section className="moderator-matching__panel">
            <header className="moderator-matching__panel-header">
              <h2>{t("runDetail.timeline.title")}</h2>
              <p className="moderator-matching__hint">{t("runDetail.timeline.hint")}</p>
            </header>

            {run.events.length === 0 ? (
              <p className="moderator-matching__empty">{t("runDetail.timeline.empty")}</p>
            ) : (
              <div className="moderator-matching-runs__timeline">
                {run.events.map((evt) => (
                  <EventRow key={`${evt.createdAt}-${evt.stage}`} evt={evt} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

const SummaryCell = ({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) => (
  <div className="moderator-matching-runs__summary-cell">
    <div className="moderator-matching-runs__stat-label">{label}</div>
    {children ?? <div className="moderator-matching-runs__stat-value">{value ?? EMPTY_VALUE}</div>}
  </div>
);

const EventRow = ({ evt }: { evt: MatchingPipelineEvent }) => {
  const { t } = useTranslation("deals");
  const isFailed = evt.stage === "failed";
  const metadata = evt.metadata as Record<string, unknown> | null | undefined;
  const hasMetadata = metadata && typeof metadata === "object" && Object.keys(metadata).length > 0;

  return (
    <article className={`moderator-matching-runs__event ${isFailed ? "moderator-matching-runs__event--failed" : ""}`}>
      <div className="moderator-matching-runs__event-head">
        <span className="moderator-matching-runs__event-stage">{stageLabel(evt.stage)}</span>
        <span className="moderator-matching-runs__event-meta">
          <span>
            <i className="fa-regular fa-clock" /> {fmtDate(evt.createdAt)}
          </span>
          <span>
            <i className="fa-solid fa-stopwatch" /> {fmtDuration(evt.durationMs)}
          </span>
        </span>
      </div>

      {isFailed && (evt.errorType || evt.errorMessage) ? (
        <div className="moderator-matching-runs__event-error">
          <strong>{evt.errorType || t("runDetail.errorFallback")}</strong>
          {evt.errorMessage ? `: ${evt.errorMessage}` : ""}
        </div>
      ) : null}

      {hasMetadata ? (
        <pre className="moderator-matching-runs__event-metadata">{JSON.stringify(metadata, null, 2)}</pre>
      ) : null}
    </article>
  );
};

export default ModeratorMatchingRunDetailPage;
